// API client for the FastAPI backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('access_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }

  getAuthHeaders() {
    return this.token ? { Authorization: `Bearer ${this.token}` } : {};
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        let errorMessage = error.detail || `HTTP ${response.status}`;

        // Handle case where detail is an object or array
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication methods
  async signUp(email, password, firstName, lastName) {
    const response = await this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, firstName, lastName }),
    });
    
    if (response.access_token) {
      this.setToken(response.access_token);
    }
    
    return response;
  }

  async signIn(email, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.access_token) {
      this.setToken(response.access_token);
    }
    
    return response;
  }

  async signOut() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.setToken(null);
    }
  }

  async getUser() {
    if (!this.token) {
      return { user: null };
    }
    
    try {
      const response = await this.request('/auth/user');
      return response;
    } catch (error) {
      // If token is invalid, clear it
      this.setToken(null);
      return { user: null };
    }
  }

  // Exercise methods
  async getExercises() {
    return await this.request('/exercises');
  }

  async getExercise(id) {
    return await this.request(`/exercises/${id}`);
  }

  // Submission methods
  async getSubmissions() {
    return await this.request('/submissions');
  }

  async createSubmission(submission) {
    return await this.request('/submissions', {
      method: 'POST',
      body: JSON.stringify(submission),
    });
  }

  // Code execution
  async executeCode(code, exercise_id) {
    return await this.request('/execute', {
      method: 'POST',
      body: JSON.stringify({ code, exercise_id }),
    });
  }

  // UI Preferences
  async getUIPreferences() {
    try {
      return await this.request('/api/ui-preferences');
    } catch (error) {
      // If not authenticated, return default preferences instead of throwing
      if (error.message.includes('Forbidden') || error.message.includes('401') || error.message.includes('403')) {
        return {
          sidebarCollapsed: false,
          selectedCategory: null,
          adminPanelLayout: 'list',
          codeEditorTheme: 'light',
          language: 'en'
        };
      }
      throw error;
    }
  }

  async updateUIPreferences(preferences) {
    return await this.request('/api/ui-preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Leaderboard methods
  async getLeaderboard({ page = 1, limit = 100 } = {}) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    return await this.request(`/api/leaderboard?${params}`);
  }

  async getUserRank() {
    return await this.request('/api/leaderboard/me');
  }

  async updateUserVisibility(userId, showOnLeaderboard) {
    return await this.request(`/api/admin/users/${userId}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ show_on_leaderboard: showOnLeaderboard }),
    });
  }

  // Generic HTTP methods for admin operations
  async delete(endpoint) {
    return await this.request(endpoint, {
      method: 'DELETE',
    });
  }
}

export const apiClient = new ApiClient();

// Convenience exports for leaderboard functionality
export const getLeaderboard = (...args) => apiClient.getLeaderboard(...args);
export const getUserRank = (...args) => apiClient.getUserRank(...args);
export const updateUserVisibility = (...args) => apiClient.updateUserVisibility(...args);